/**
 * tax/ReportRequest.tsx — 임지도사 협업방 「정부지원금 리포트 요청하기」 (2026-10-09 지도사)
 *  2026-10-09 디자인 시안(zip 「정부지원금 리포트 요청」) 적용: 「받을 수 있는 정부지원금, 리포트로 정리해 드릴게요」 · 1/3 ~ 3/3 · 아래 고정 버튼
 *  ① 사업자등록번호 ② 참고할 내용(대표님 관심 등 · 선택) ③ 요청하는 분(성함 · 연락처) + 개인정보 동의
 *  보내면 로움 일일 업무(평일 가장 가까운 날 · 담당 오재인 + 임우승)로 들어간다 — backend routes/tax.js POST /report
 *  요청자 성함 · 연락처 · 사무소는 소식지 「보내는 곳」에 저장한 값으로 미리 채운다
 */
import React from 'react';
import { X, KInput, inputSt, useWide, Shell, Section } from './taxUi';
import { load, forget, OFFICE_KEY } from './taxApi';
import { digits, fmtBiz, phone, bizOk } from './taxForm';
import { Field, Agree, Done, BottomCta, SavedNote } from './LoanRequest';
import type { Progress } from './TaxApp';

interface Office { office: string; person: string; tel: string; mob: string }
type Step = 'biz' | 'req' | 'agree';
const NOTE_MAX = 1000;

export default function ReportRequest({ onHome, onProgress }: { onHome: () => void; onProgress: (p: Progress | null) => void }) {
  const wide = useWide(768);
  const [saved, setSaved] = React.useState<Office>(() => load<Office>(OFFICE_KEY, { office: '', person: '', tel: '', mob: '' }));
  const [biz, setBiz] = React.useState('');
  const [note, setNote] = React.useState('');
  const [reqName, setReqName] = React.useState(saved.person || '');
  const [reqTel, setReqTel] = React.useState(saved.mob || saved.tel || '');
  const [agree, setAgree] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const refs = React.useRef<Partial<Record<Step, HTMLElement | null>>>({});

  const okBiz = bizOk(biz);
  const okReq = !!reqName.trim() && digits(reqTel, 11).length >= 9;
  const left = [okBiz, okReq].filter((x) => !x).length;
  const ready = left === 0 && agree;
  React.useEffect(() => {
    onProgress(done ? { label: '접수 완료', pct: 100 } : { label: `${2 - left} / 2 필수 완료`, pct: (2 - left) * 50 });
  }, [left, done, onProgress]);

  const jump = (k: Step) => {
    const el = refs.current[k];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const i = k === 'agree' ? el : el.querySelector('input,textarea,button');
    if (i instanceof HTMLElement) window.setTimeout(() => i.focus({ preventScroll: true }), 300);
  };
  const submit = async () => {
    if (busy) return;
    if (!ready) { jump(!okBiz ? 'biz' : !okReq ? 'req' : 'agree'); return; }
    setBusy(true); setMsg('');
    try {
      const r = await fetch('/api/tax/report', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ biz: digits(biz, 10), note: note.trim(), reqName: reqName.trim(), reqTel, office: saved.office || '', agree: true }),
      });
      const j = await r.json().catch(() => null) as { success?: boolean; error?: string } | null;
      if (!r.ok || !j || !j.success) throw new Error((j && j.error) || '접수하지 못했어요 — 잠시 뒤 다시 시도해 주세요');
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) { setMsg(e instanceof Error ? e.message : '접수하지 못했어요'); }
    finally { setBusy(false); }
  };
  const set = (k: Step) => (el: HTMLElement | null) => { refs.current[k] = el; };

  if (done) {
    return (
      <Done title="리포트 요청을 보냈어요" onHome={onHome}>
        받을 수 있는 정부지원금을 정리해서<br /><b style={{ color: X.text }}>{reqTel}</b>로 리포트를 보내드릴게요.
      </Done>
    );
  }

  return (
    <Shell onHome={onHome} title={<>받을 수 있는 정부지원금,<br />리포트로 정리해 드릴게요</>} sub="사업자등록번호만 있으면 바로 알아볼 수 있어요." wide={wide} max={640}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 170 }}>
        <div ref={set('biz')}>
          <Section n={1} of={3} title="사업자 정보">
            <Field label="사업자등록번호" extra={digits(biz, 10).length === 10 && !okBiz ? <span style={{ fontSize: 13, color: X.red }}>번호를 다시 확인해 주세요</span> : null}>
              <KInput inputMode="numeric" value={biz} placeholder="000-00-00000" onChange={(v) => setBiz(fmtBiz(v))} />
            </Field>
          </Section>
        </div>

        <Section n={2} of={3} title="참고할 내용" sideHint="선택" hint="관심 분야나 계획을 알려주시면 더 맞춤으로 찾아드려요.">
          <textarea aria-label="참고할 내용" value={note} maxLength={NOTE_MAX} rows={5}
            placeholder="예) 대표님이 연구개발 과제와 스마트공장 지원에 관심이 많아요. 내년에 공장 증설 계획이 있어요."
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
            style={{ ...inputSt, height: 'auto', minHeight: 150, padding: '14px 16px', fontSize: 16, lineHeight: 1.55, resize: 'vertical' }} />
          <div style={{ fontSize: 13, color: X.muted, textAlign: 'right', marginTop: 6 }}>{note.length} / {NOTE_MAX}</div>
        </Section>

        <div ref={set('req')}>
          <Section n={3} of={3} title="요청하는 분" hint="완성된 리포트를 이 연락처로 보내드려요.">
            {(saved.office || saved.person || saved.mob || saved.tel) && <div style={{ marginBottom: 16 }}><SavedNote saved={saved} onForget={() => { forget(OFFICE_KEY); setSaved({ office: '', person: '', tel: '', mob: '' }); setReqName(''); setReqTel(''); }} /></div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16 }}>
              <Field label="이름"><KInput value={reqName} maxLength={20} placeholder="홍길동" autoComplete="name" onChange={setReqName} /></Field>
              <Field label="연락처"><input inputMode="tel" autoComplete="tel" value={reqTel} placeholder="010-0000-0000" onChange={(e) => setReqTel(phone(e.target.value))} style={inputSt} /></Field>
            </div>
          </Section>
        </div>

        <Agree on={agree} onToggle={() => setAgree(!agree)} refEl={set('agree')} text="정부지원금 리포트 작성을 위해 사업자등록번호와 요청자 연락처를 수집·이용해요." />

        {msg && <div role="alert" style={{ fontSize: 15, color: X.red, background: X.redLt, borderRadius: 12, padding: '12px 14px' }}>{msg}</div>}
      </div>

      <BottomCta note="요청 내용은 임지도사가 직접 확인해요" ready={ready} busy={busy} onClick={submit}
        label={busy ? '보내는 중…' : ready ? '리포트 요청 보내기' : left ? `${left}개 항목을 더 채워주세요` : '동의 후 보낼 수 있어요'} />
    </Shell>
  );
}
